/**
 * 公共格式化工具函数
 *
 * 集中管理项目中各组件共用的格式化逻辑，避免重复定义。
 */

/**
 * 格式化文件大小为人类可读的字符串
 * @param bytes - 文件大小（字节），可为 null/undefined
 * @returns 格式化后的字符串，如 "1.5 MB"
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return '-'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 格式化日期时间为标准字符串（含秒）
 * 输出格式: "2025-01-15 14:30:00"
 * @param dateStr - ISO 日期字符串
 * @returns 格式化后的日期时间字符串
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

/**
 * 格式化日期时间为简短字符串（不含秒）
 * 输出格式: "2025-01-15 14:30"
 * @param dateStr - ISO 日期字符串
 * @returns 格式化后的日期时间字符串
 */
export function formatDateTimeShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day} ${hour}:${minute}`
}

/**
 * 格式化相对时间（如 "3分钟前"、"2天前"）
 * @param dateStr - ISO 日期字符串
 * @returns 相对时间字符串
 */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  const months = Math.floor(diff / 2592000000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 30) return `${days}天前`
  if (months < 12) return `${months}月前`
  return `${Math.floor(months / 12)}年前`
}

/**
 * 格式化创建人显示（UUID 格式显示前 8 位 + ...）
 * @param creator - 创建人标识符（可能是 UUID 或用户名）
 * @returns 格式化后的创建人字符串
 */
export function formatCreator(creator: string | null | undefined): string {
  if (!creator) return '-'
  // 判断是否是 UUID 格式（36位字符，包含4个-）
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (uuidRegex.test(creator)) {
    return creator.substring(0, 8) + '...'
  }
  // 如果长度超过12位，截取并显示省略号
  if (creator.length > 12) {
    return `${creator.slice(0, 8)}...`
  }
  return creator
}

/**
 * 格式化视频/音频时长
 * @param seconds - 时长秒数
 * @returns 格式化后的时长字符串，如 "3:05"
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return ''
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 格式化毫秒级耗时（平均延迟等），自动升级单位避免 81153.75ms 这种长串
 * @param ms - 毫秒
 * @returns "850ms" / "3.2s" / "1.4min"；null/undefined → "—"
 */
export function formatLatencyMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}min`
}

/**
 * 获取文件名（去掉后缀）
 * @param fileName - 完整文件名
 * @returns 去掉扩展名的文件名
 */
export function getFileNameWithoutExt(fileName: string | null | undefined): string {
  if (!fileName) return '-'
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot <= 0) return fileName
  return fileName.substring(0, lastDot)
}

/**
 * 判断文件名是否为图片文件
 * @param fileName - 文件名
 * @returns 是否为图片
 */
export function isImageFile(fileName: string | null | undefined): boolean {
  if (!fileName) return false
  const ext = fileName.toLowerCase().split('.').pop() || ''
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif'].includes(ext)
}

/**
 * 判断文件名是否为视频文件
 * @param fileName - 文件名
 * @returns 是否为视频
 */
export function isVideoFile(fileName: string | null | undefined): boolean {
  if (!fileName) return false
  const ext = fileName.toLowerCase().split('.').pop() || ''
  return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', '3gp'].includes(ext)
}
